import apiClient from '../utils/apiClient';

export interface NotificationPagination {
  page?: number;
  limit?: number;
  sort?: string;
  search?: string;
  type?: string;
  isRead?: boolean;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data: T;
}

export interface Notification {
  _id: string;
  title: string;
  message: string;
  type: string;
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
  data?: any;
  context?: 'user_panel' | 'seller_panel' | 'admin_panel';
  targetRole?: 'user' | 'seller' | 'admin';
}

export interface NotificationStats {
  total: number;
  unread: number;
  read: number;
  byType: Record<string, number>;
  byDate: Record<string, number>;
}

export interface NotificationListResponse {
  notifications: Notification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats: NotificationStats;
}

// انواع نوتیفیکیشن‌های ادمین
export const ADMIN_NOTIFICATION_TYPES = {
  NEW_USER_REGISTERED: 'new_user_registered',
  NEW_SELLER_APPLICATION: 'new_seller_application',
  SELLER_VERIFICATION_REQUEST: 'seller_verification_request',
  NEW_PRODUCT_PENDING: 'new_product_pending',
  NEW_WITHDRAWAL_REQUEST: 'new_withdrawal_request',
  SYSTEM_ERROR: 'system_error',
  DISPUTE_RAISED: 'dispute_raised',
  NEW_TICKET: 'new_ticket',
  TICKET_ESCALATED: 'ticket_escalated',
  PAYMENT_ISSUE: 'payment_issue',
  SECURITY_ALERT: 'security_alert',
  BANK_ACCOUNT_VERIFICATION: 'bank_account_verification'
} as const;

// Default values for error responses
const DEFAULT_NOTIFICATION: Notification = {
  _id: '',
  title: '',
  message: '',
  type: '',
  isRead: false,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

const DEFAULT_STATS: NotificationStats = {
  total: 0,
  unread: 0,
  read: 0,
  byType: {},
  byDate: {}
};

const DEFAULT_LIST_RESPONSE: NotificationListResponse = {
  notifications: [],
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  },
  stats: DEFAULT_STATS
};

export const notificationApi = {
  // Get notifications with optional context filtering
  getNotifications: async (context?: string, pagination?: NotificationPagination): Promise<ApiResponse<NotificationListResponse>> => {
    try {
      const params = new URLSearchParams();
      
      if (context) params.append('context', context);
      if (pagination?.page) params.append('page', pagination.page.toString());
      if (pagination?.limit) params.append('limit', pagination.limit.toString());
      if (pagination?.sort) params.append('sort', pagination.sort);
      if (pagination?.search) params.append('search', pagination.search);
      if (pagination?.type) params.append('type', pagination.type);
      if (pagination?.isRead !== undefined) params.append('isRead', pagination.isRead.toString());

      const response = await apiClient.get(`/notifications?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در دریافت نوتیفیکیشن‌ها',
        data: DEFAULT_LIST_RESPONSE
      };
    }
  },

  // Get admin panel notifications specifically
  getAdminPanelNotifications: async (pagination?: NotificationPagination): Promise<ApiResponse<NotificationListResponse>> => {
    return notificationApi.getNotifications('admin_panel', pagination);
  },

  // Get notification by ID
  getNotificationById: async (notificationId: string): Promise<ApiResponse<Notification>> => {
    try {
      const response = await apiClient.get(`/notifications/${notificationId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در دریافت جزئیات نوتیفیکیشن',
        data: DEFAULT_NOTIFICATION
      };
    }
  },

  // Mark notification as read
  markAsRead: async (notificationId: string): Promise<ApiResponse<Notification>> => {
    try {
      const response = await apiClient.patch(`/notifications/${notificationId}/read`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در علامت‌گذاری نوتیفیکیشن',
        data: DEFAULT_NOTIFICATION
      };
    }
  },

  // Mark notification as unread
  markAsUnread: async (notificationId: string): Promise<ApiResponse<Notification>> => {
    try {
      const response = await apiClient.patch(`/notifications/${notificationId}/unread`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در علامت‌گذاری نوتیفیکیشن به عنوان خوانده نشده',
        data: DEFAULT_NOTIFICATION
      };
    }
  },

  // Mark multiple notifications as read
  markMultipleAsRead: async (notificationIds: string[]): Promise<ApiResponse<{ updated: number }>> => {
    try {
      const response = await apiClient.patch('/notifications/mark-multiple-read', { notificationIds });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در علامت‌گذاری چندین نوتیفیکیشن',
        data: { updated: 0 }
      };
    }
  },

  // Mark all notifications as read for a specific context
  markAllAsRead: async (context: string): Promise<ApiResponse<{ updated: number }>> => {
    try {
      const response = await apiClient.patch('/notifications/mark-all-read', { context });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در علامت‌گذاری همه نوتیفیکیشن‌ها',
        data: { updated: 0 }
      };
    }
  },

  // Delete notification
  deleteNotification: async (notificationId: string): Promise<ApiResponse<{ deleted: boolean }>> => {
    try {
      const response = await apiClient.delete(`/notifications/${notificationId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در حذف نوتیفیکیشن',
        data: { deleted: false }
      };
    }
  },

  // Delete multiple notifications
  deleteMultipleNotifications: async (notificationIds: string[]): Promise<ApiResponse<{ deleted: number }>> => {
    try {
      const response = await apiClient.delete('/notifications/delete-multiple', { data: { notificationIds } });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در حذف چندین نوتیفیکیشن',
        data: { deleted: 0 }
      };
    }
  },

  // Get unread count
  getUnreadCount: async (context?: string): Promise<ApiResponse<{ count: number }>> => {
    try {
      const params = context ? `?context=${context}` : '';
      const response = await apiClient.get(`/notifications/unread-count${params}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در دریافت تعداد نوتیفیکیشن‌های خوانده نشده',
        data: { count: 0 }
      };
    }
  },

  // Clear all notifications for a context
  clearAllNotifications: async (context: string): Promise<ApiResponse<{ deleted: number }>> => {
    try {
      const response = await apiClient.delete(`/notifications/clear-all`, { data: { context } });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در پاک کردن همه نوتیفیکیشن‌ها',
        data: { deleted: 0 }
      };
    }
  },

  // Get notification statistics
  getStats: async (context?: string): Promise<ApiResponse<NotificationStats>> => {
    try {
      const params = context ? `?context=${context}` : '';
      const response = await apiClient.get(`/notifications/stats${params}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در دریافت آمار نوتیفیکیشن‌ها',
        data: DEFAULT_STATS
      };
    }
  },

  // Create notification (Admin only)
  createNotification: async (notificationData: {
    targetUserId: string;
    title: string;
    message: string;
    type: string;
    data?: any;
  }): Promise<ApiResponse<Notification>> => {
    try {
      const response = await apiClient.post('/notifications', notificationData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در ایجاد نوتیفیکیشن',
        data: DEFAULT_NOTIFICATION
      };
    }
  },

  // Send bulk notifications (Admin only)
  sendBulkNotifications: async (notificationData: {
    targetRole: 'user' | 'seller' | 'admin';
    title: string;
    message: string;
    type: string;
    data?: any;
  }): Promise<ApiResponse<{ sent: number }>> => {
    try {
      const response = await apiClient.post('/notifications/bulk', notificationData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در ارسال نوتیفیکیشن‌های گروهی',
        data: { sent: 0 }
      };
    }
  },

  // Get notification types
  getNotificationTypes: async (): Promise<ApiResponse<{ types: string[] }>> => {
    try {
      const response = await apiClient.get('/notifications/types');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در دریافت انواع نوتیفیکیشن',
        data: { types: [] }
      };
    }
  },

  // Register this browser's FCM web-push token so the backend can push even when
  // the admin panel tab is closed. Safe no-op response on failure.
  registerDeviceToken: async (token: string, platform = 'web'): Promise<ApiResponse<{ registered: boolean }>> => {
    try {
      const response = await apiClient.post('/notifications/device-token', { token, platform });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در ثبت توکن پوش',
        data: { registered: false }
      };
    }
  },

  // Export notifications (Admin only)
  exportNotifications: async (context?: string, filters?: NotificationPagination): Promise<Blob> => {
    try {
      const params = new URLSearchParams();
      if (context) params.append('context', context);
      if (filters?.type) params.append('type', filters.type);
      if (filters?.isRead !== undefined) params.append('isRead', filters.isRead.toString());

      const response = await apiClient.get(`/notifications/export?${params.toString()}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'خطا در دانلود گزارش نوتیفیکیشن‌ها');
    }
  }
}; 